import React from "react";
import type { DateValue } from "react-aria";
import { useLocale, useRangeCalendar } from "react-aria";
import type { CalendarDate } from "@internationalized/date";
import { createCalendar } from "@internationalized/date";
import type { RangeCalendarStateOptions } from "react-stately";
import { useRangeCalendarState } from "react-stately";
import CalendarHeader from "./CalendarHeader";
import { CalendarGrid } from "../DatePicker/Calendar";
import { styled } from "@linaria/react";
import type { Preset } from "./PresetSelector";

const GridWrapper = styled.div`
  display: flex;
`;

function RangeCalendar<T extends DateValue = DateValue>(
  props: Partial<RangeCalendarStateOptions<T>> & {
    hasTime?: boolean;
    preset: Preset<DateValue> | null;
  },
) {
  const { locale } = useLocale();
  const { hasTime } = props;
  const state = useRangeCalendarState({
    ...props,
    visibleDuration: { months: 2 },
    locale,
    createCalendar,
  });

  const ref = React.useRef(null);
  const { calendarProps, prevButtonProps, nextButtonProps } = useRangeCalendar(props, state, ref);

  React.useEffect(() => {
    if (props.preset) {
      const end =
        typeof props.preset.endDate === "function" ? props.preset.endDate() : props.preset.endDate;
      state.setFocusedDate(end as CalendarDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.preset]);

  return (
    <div {...calendarProps} ref={ref}>
      <CalendarHeader
        state={state}
        calendarProps={calendarProps}
        prevButtonProps={prevButtonProps}
        nextButtonProps={nextButtonProps}
      />
      <GridWrapper>
        <div>
          <CalendarGrid state={state} />
          {hasTime && <input type="time" />}
        </div>
        <div>
          <CalendarGrid state={state} offset={{ months: 1 }} />
          {hasTime && <input type="time" />}
        </div>
      </GridWrapper>
    </div>
  );
}

export default RangeCalendar;
